import { ROUTES } from '@compflash/contracts/routes';
export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.name = 'ApiError';
    }
    get isQuotaExceeded() {
        return this.code === 'quota_exceeded';
    }
    get isUnauthorized() {
        return this.status === 401;
    }
    /**
     * The request never reached the server: DNS failure, offline, CORS, or the
     * API simply not being deployed yet. Status 0 because there was no response
     * to take one from.
     */
    get isUnreachable() {
        return this.status === 0;
    }
}
export class CompFlashClient {
    options;
    fetchImpl;
    refreshInFlight = null;
    constructor(options) {
        this.options = options;
        this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
    }
    get baseUrl() {
        return this.options.baseUrl;
    }
    /** True when a session exists locally. Avoids pointless 401s on anonymous page loads. */
    hasSession() {
        return this.options.tokens.read() !== null;
    }
    /* --------------------------------------------------------------- auth -- */
    async register(email, password, displayName) {
        const response = await this.request('POST', ROUTES.register, { email, password, displayName }, false);
        this.options.tokens.write(response);
        return response;
    }
    async login(email, password) {
        const response = await this.request('POST', ROUTES.login, { email, password }, false);
        this.options.tokens.write(response);
        return response;
    }
    /**
     * Always resolves for a well-formed address, whether or not an account
     * exists. The server refuses to say, so there is nothing here to branch on.
     */
    async requestPasswordReset(email) {
        await this.request('POST', ROUTES.passwordResetRequest, { email }, false);
    }
    /**
     * Deliberately does not sign the user in. Confirming a reset revokes every
     * refresh token, so the new password gets exercised once immediately —
     * which is also how a typo in a password manager surfaces now rather than
     * on the next device.
     */
    async confirmPasswordReset(token, password) {
        await this.request('POST', ROUTES.passwordResetConfirm, { token, password }, false);
    }
    async logout() {
        try {
            await this.request('POST', ROUTES.logout, {});
        }
        finally {
            this.options.tokens.write(null);
            this.options.onSignedOut?.();
        }
    }
    async me() {
        return this.request('GET', ROUTES.me);
    }
    async updateProfile(patch) {
        return this.request('PATCH', ROUTES.me, patch);
    }
    /* ------------------------------------------------------------- system -- */
    async config() {
        return this.request('GET', ROUTES.config, undefined, false);
    }
    async status() {
        return this.request('GET', ROUTES.status, undefined, false);
    }
    async plans() {
        return this.request('GET', ROUTES.plans, undefined, false);
    }
    /* -------------------------------------------------------------- scans -- */
    async createScan(request) {
        return this.request('POST', ROUTES.scans, request);
    }
    /**
     * Create a scan and receive every pipeline stage as it completes.
     *
     * Uses a streaming POST rather than a create-then-subscribe pair so the first
     * useful result is one round trip away, which is the whole point during a live
     * auction. Falls back to a single JSON response if the server does not stream.
     */
    async createScanStreaming(request, onStage) {
        const headers = await this.headers(true);
        headers.set('Accept', 'text/event-stream');
        const response = await this.fetchImpl(`${this.options.baseUrl}${ROUTES.scans}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(request),
        });
        if (!response.ok)
            throw await this.toError(response);
        if (!response.body || !String(response.headers.get('content-type')).includes('text/event-stream')) {
            const scan = (await response.json());
            onStage(scan);
            return scan;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let latest = null;
        for (;;) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
                const frame = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);
                boundary = buffer.indexOf('\n\n');
                if (!frame.startsWith('data: '))
                    continue;
                const event = JSON.parse(frame.slice(6));
                if (event.error)
                    throw new ApiError(500, event.error.code, event.error.message);
                if (event.scan) {
                    latest = event.scan;
                    onStage(event.scan);
                }
            }
        }
        if (!latest)
            throw new ApiError(500, 'internal_error', 'The scan produced no result.');
        return latest;
    }
    async getScan(id) {
        return this.request('GET', ROUTES.scan(id));
    }
    async listScans(options = {}) {
        const query = new URLSearchParams();
        if (options.limit)
            query.set('limit', String(options.limit));
        if (options.saved)
            query.set('saved', 'true');
        if (options.before)
            query.set('before', options.before);
        const suffix = query.toString() ? `?${query}` : '';
        return this.request('GET', `${ROUTES.scans}${suffix}`);
    }
    async setScanSaved(id, saved) {
        await this.request('POST', ROUTES.scanSave(id), { saved });
    }
    async deleteScan(id) {
        await this.request('DELETE', ROUTES.scan(id));
    }
    async sendFeedback(id, feedback) {
        return this.request('POST', ROUTES.scanFeedback(id), feedback);
    }
    /* ------------------------------------------------------------- market -- */
    async valuate(request) {
        return this.request('POST', ROUTES.valuations, request);
    }
    async submitComp(comp) {
        return this.request('POST', ROUTES.comps, comp);
    }
    async listComps() {
        return this.request('GET', ROUTES.comps);
    }
    async deleteComp(id) {
        await this.request('DELETE', ROUTES.comp(id));
    }
    async watchlist() {
        return this.request('GET', ROUTES.watchlist);
    }
    async addToWatchlist(item) {
        return this.request('POST', ROUTES.watchlist, item);
    }
    async removeFromWatchlist(id) {
        await this.request('DELETE', ROUTES.watchlistItem(id));
    }
    async searchScans(query) {
        return this.request('GET', `${ROUTES.cardSearch}?q=${encodeURIComponent(query)}`);
    }
    /* ------------------------------------------------------------ billing -- */
    async entitlements() {
        return this.request('GET', ROUTES.entitlements);
    }
    async usage() {
        return this.request('GET', ROUTES.usage);
    }
    async startCheckout(planId, interval) {
        return this.request('POST', ROUTES.billingCheckout, { planId, interval });
    }
    /* ------------------------------------------------------------ privacy -- */
    async exportData() {
        return this.request('GET', ROUTES.privacyExport);
    }
    async deleteAccount() {
        return this.request('POST', ROUTES.privacyDelete, { confirm: true });
    }
    /* ---------------------------------------------------------- internals -- */
    async headers(authenticated) {
        const headers = new Headers({ 'Content-Type': 'application/json' });
        if (!authenticated)
            return headers;
        const tokens = await this.freshTokens();
        if (tokens)
            headers.set('Authorization', `Bearer ${tokens.accessToken}`);
        return headers;
    }
    /** Refresh proactively, and only once even if several calls race. */
    async freshTokens() {
        const current = this.options.tokens.read();
        if (!current)
            return null;
        if (Date.parse(current.expiresAt) - Date.now() > 60_000)
            return current;
        if (!this.refreshInFlight) {
            this.refreshInFlight = this.performRefresh(current.refreshToken).finally(() => {
                this.refreshInFlight = null;
            });
        }
        return this.refreshInFlight;
    }
    async performRefresh(refreshToken) {
        const response = await this.fetchImpl(`${this.options.baseUrl}${ROUTES.refresh}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) {
            this.options.tokens.write(null);
            this.options.onSignedOut?.();
            return null;
        }
        const refreshed = (await response.json());
        this.options.tokens.write(refreshed);
        return refreshed;
    }
    async request(method, path, body, authenticated = true) {
        const headers = await this.headers(authenticated);
        let response;
        try {
            response = await this.fetchImpl(`${this.options.baseUrl}${path}`, {
                method,
                headers,
                body: body === undefined ? undefined : JSON.stringify(body),
            });
        }
        catch (cause) {
            // fetch rejects rather than resolving when the request never reaches the
            // server. Left raw, the user is shown "Failed to fetch", which reads as a
            // bug in the page rather than as a service that is unreachable.
            throw new ApiError(0, 'service_unreachable', 'Could not reach CompFlash. Check your connection and try again in a moment.', cause);
        }
        if (response.status === 204)
            return undefined;
        if (!response.ok) {
            const error = await this.toError(response);
            if (error.isUnauthorized && authenticated)
                this.options.onSignedOut?.();
            throw error;
        }
        return (await response.json());
    }
    async toError(response) {
        const body = (await response.json().catch(() => null));
        return new ApiError(response.status, body?.error?.code ?? 'http_error', body?.error?.message ?? `Request failed with status ${response.status}.`, body?.error?.details);
    }
}
/** localStorage-backed token store for browser apps. */
export function browserTokenStore(key = 'compflash.session') {
    return {
        read() {
            try {
                const raw = globalThis.localStorage?.getItem(key);
                return raw ? JSON.parse(raw) : null;
            }
            catch {
                return null;
            }
        },
        write(tokens) {
            try {
                if (tokens)
                    globalThis.localStorage?.setItem(key, JSON.stringify(tokens));
                else
                    globalThis.localStorage?.removeItem(key);
            }
            catch {
                // Storage can be unavailable in private mode; the session simply does not persist.
            }
        },
    };
}
/** In-memory store, for tests and short-lived contexts. */
export function memoryTokenStore(initial = null) {
    let value = initial;
    return {
        read: () => value,
        write: (tokens) => {
            value = tokens;
        },
    };
}
//# sourceMappingURL=index.js.map